import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Tooltip, Modal, Fade, Box, TextField, Button, IconButton, CircularProgress, Menu, MenuItem, ListItemIcon } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { toast } from 'react-toastify';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSelectedWorkspace, useWorkspaces } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import { TabIndex } from '../navigation';
import { ConversationTypes, SystemEventTypes } from '@flashbacktech/flashbackclient/api';
import CloudGatewayIntroModal from '../components/CloudGatewayIntroModal';
import CloudGatewayTutorialLauncher from '../components/CloudGatewayTutorialLauncher';
import Security2FARequiredModal from '../components/Security2FARequiredModal';
import Security2FAWarning from '../components/Security2FAWarning';

// Import icons
import docsSvg from '../assets/icons-home/docs.svg';
import supportSvg from '../assets/icons-home/support.svg';
import bellSvg from '../assets/icons-home/bell.svg';
import checkNotifsIcon from '../assets/notifications/check-notifs.svg';
import reloadNotifsIcon from '../assets/notifications/reload-notifs.svg';
import warningNotifsIcon from '../assets/notifications/warning-notifs.svg';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import ProfileModal from '../components/ProfileModal';
import UpgradePromptModal from '../components/UpgradePromptModal';
import CreateWorkspaceModal from '../components/CreateWorkspaceModal';
import LockIcon from '@mui/icons-material/Lock';

// Custom SVG Icon components (matching sidebar icons)
const ChatIconCustom = ({ size = 44, color = 'white' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
  </svg>
);

const OrganizationIcon = ({ size = 44, color = 'white' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="16" y="16" width="6" height="6" rx="1" />
    <rect x="2" y="16" width="6" height="6" rx="1" />
    <rect x="9" y="2" width="6" height="6" rx="1" />
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
    <path d="M12 12V8" />
  </svg>
);

const HubIcon = ({ size = 44, color = 'white' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);

const DepinHubIcon = ({ size = 44, color = 'white' }: { size?: number; color?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <line x1="12" y1="9" x2="5" y2="7" />
    <line x1="12" y1="9" x2="19" y2="7" />
    <line x1="12" y1="15" x2="5" y2="17" />
    <line x1="12" y1="15" x2="19" y2="17" />
  </svg>
);

// Support Modal Component (same as Header.tsx)
interface SupportModalProps {
  open: boolean;
  onClose: () => void;
  apiClient: any;
  withTokenRefresh: any;
  userEmail: string;
}

const SupportModal: React.FC<SupportModalProps> = ({ open, onClose, apiClient, withTokenRefresh, userEmail }) => {
  const [email, setEmail] = React.useState(userEmail);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const MAX_CHARS = 1000;
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  React.useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError('File size exceeds 25 MB limit');
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setAttachment(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClear = () => {
    setSubject('');
    setMessage('');
    handleRemoveFile();
  };

  const handleSubmit = async () => {
    if (!email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (message.length > MAX_CHARS) {
      toast.error(`Message exceeds ${MAX_CHARS} characters`);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      if (attachment) formData.append('attachment', attachment);

      const response = await withTokenRefresh(() => apiClient.sendFeedbackEmail(formData));
      if ((response as any)?.success) {
        toast.success('Your message has been sent successfully');
        handleClear();
        onClose();
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Fade in={open}>
        <Box sx={{ width: '100%', maxWidth: 480, bgcolor: '#FFFFFF', borderRadius: '16px', boxShadow: '0px 20px 60px rgba(0,0,0,0.15)', outline: 'none', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #E5E7EB' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#617BFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SendIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Contact Support</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>We'll get back to you shortly</Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F3F4F6' } }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Form */}
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Email */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Email</Typography>
              <TextField
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Subject */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Subject</Typography>
              <TextField
                fullWidth
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description"
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Message */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054' }}>Message</Typography>
                <Typography sx={{ fontSize: '11px', color: message.length > MAX_CHARS * 0.9 ? '#F59E0B' : '#9CA3AF' }}>{message.length}/{MAX_CHARS}</Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => e.target.value.length <= MAX_CHARS && setMessage(e.target.value)}
                placeholder="Describe your issue or feedback..."
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Attachment */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Attachment (optional)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '8px', borderColor: '#E5E7EB', color: '#667085', textTransform: 'none', fontSize: '13px', '&:hover': { borderColor: '#617BFF', bgcolor: '#F0F2FF' } }}
                >
                  Choose file
                  <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} />
                </Button>
                {attachment && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#F3F4F6', borderRadius: '6px' }}>
                    <Typography sx={{ fontSize: '12px', color: '#344054', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.name}</Typography>
                    <IconButton size="small" onClick={handleRemoveFile} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} /></IconButton>
                  </Box>
                )}
              </Box>
              {fileError && <Typography sx={{ fontSize: '11px', color: '#EF4444', mt: 0.5 }}>{fileError}</Typography>}
              <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>Max 25 MB</Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2.5, borderTop: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
            <Button onClick={handleClear} disabled={isSubmitting} sx={{ borderRadius: '8px', textTransform: 'none', color: '#667085', fontSize: '13px', fontWeight: 600, px: 2 }}>Clear</Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !email || !subject || !message}
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#617BFF', fontSize: '13px', fontWeight: 600, px: 2.5, '&:hover': { bgcolor: '#5169E8' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

interface HubProps {
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const Hub: React.FC<HubProps> = ({ handleTabChange }) => {
  const { globalData, authState, handleSignOut, setSelectedWorkspace } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const workspaces = useWorkspaces();
  const { mfaState } = useMFA();
  const navigate = useNavigate();
  
  // Use centralized workspace hook
  const currentWorkspaceId = useSelectedWorkspace();
  
  // Conditions for hiding quicklink cards
  const hasNoWorkspaces = (globalData?.workspaces?.length || 0) === 0;
  const isIndividualAccount = !globalData?.hasOrgAccess;
  
  // MFA state
  const hasAnyMfaConfigured = (mfaState?.methods?.some((m) => m.isConfigured) ?? false);
  const is2FAEnabled = mfaState?.status?.isEnabled || hasAnyMfaConfigured;
  const hasWorkspaces = workspaces && workspaces.length > 0;
  
  // Unified setup banner state (synced with App.tsx via localStorage)
  const [isSetupBannerDismissed, setIsSetupBannerDismissed] = useState(() => {
    try {
      const lastDismissed = localStorage.getItem('setupBannerDismissedAt');
      if (lastDismissed) {
        const elapsed = Date.now() - parseInt(lastDismissed, 10);
        return elapsed < 30 * 60 * 1000; // Within 30-min cooldown
      }
    } catch { /* ignore */ }
    return false;
  });
  const [isSetupBannerVisible, setIsSetupBannerVisible] = useState(false);

  useEffect(() => {
    if (is2FAEnabled) { setIsSetupBannerVisible(false); return; }
    if (isSetupBannerDismissed) return;
    const timer = setTimeout(() => setIsSetupBannerVisible(true), 60 * 1000);
    return () => clearTimeout(timer);
  }, [is2FAEnabled, isSetupBannerDismissed]);

  const handleSetupBannerDismiss = () => {
    setIsSetupBannerDismissed(true);
    try { localStorage.setItem('setupBannerDismissedAt', String(Date.now())); } catch { /* ignore */ }
  };

  const isProfileIncomplete = !globalData?.userProfile?.firstName || !globalData?.userProfile?.lastName;
  
  // Flashgate intro modal state
  const [showGatewayIntro, setShowGatewayIntro] = useState(false);
  const [showTutorialLauncher, setShowTutorialLauncher] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [show2FARequiredModal, setShow2FARequiredModal] = useState(false);
  
  // Check if gateway intro was completed
  const isGatewayIntroCompleted = (() => {
    try { return localStorage.getItem('cloudGatewayIntroCompleted') === 'true'; } catch { return false; }
  })();
  
  // Check if tutorial was dismissed or completed
  const isTutorialDismissed = (() => {
    try { return localStorage.getItem('gatewayTutorialDismissed') === 'true'; } catch { return false; }
  })();
  const isTutorialCompleted = (() => {
    try { return localStorage.getItem('gatewayTutorialCompleted') === 'true'; } catch { return false; }
  })();
  
  // Get saved tutorial step
  const getSavedTutorialStep = () => {
    try {
      const saved = localStorage.getItem('gatewayTutorialStep');
      return saved ? parseInt(saved, 10) : 0;
    } catch { return 0; }
  };
  
  // Flashgate intro modal logic
  useEffect(() => {
    // Wait for global data to be ready before showing intro modal
    if (!globalData.isReady) return;
    
    // Show intro modal if:
    // 1. User has no workspaces
    // 2. Intro was not completed before
    if (!hasWorkspaces && !isGatewayIntroCompleted) {
      if (!is2FAEnabled) {
        // If 2FA not enabled, show 2FA required modal
        setShow2FARequiredModal(true);
        return;
      }
      setShowGatewayIntro(true);
    } else {
      // Hide intro if workspaces exist
      setShowGatewayIntro(false);
    }
    
    // Show tutorial launcher if tutorial not completed (and intro is done)
    // Add a 1.5 second delay to prevent element visibility issues
    if (isGatewayIntroCompleted && !isTutorialCompleted && !isTutorialDismissed) {
      const tutorialTimer = setTimeout(() => {
        setShowTutorialLauncher(true);
        setTutorialStep(getSavedTutorialStep());
      }, 1500);
      return () => clearTimeout(tutorialTimer);
    }
  }, [globalData.isReady, hasWorkspaces, isGatewayIntroCompleted, is2FAEnabled, isTutorialDismissed, isTutorialCompleted]);
  
  // Handle 2FA setup from modal
  const handle2FASetup = () => {
    setShow2FARequiredModal(false);
    // Trigger profile security section with highlight2FA
    window.dispatchEvent(new CustomEvent('openProfileModal', { 
      detail: { section: 'Profile', tab: 'Security', highlight2FA: true } 
    }));
  };
  
  // Handle Gateway Intro completion
  const handleGatewayIntroComplete = () => {
    try { localStorage.setItem('cloudGatewayIntroCompleted', 'true'); } catch { /* ignore */ }
    setShowGatewayIntro(false);
    setShowTutorialLauncher(true);
  };
  
  // Handle workspace creation from intro modal
  const handleCreateWorkspaceFromIntro = async (name: string) => {
    try {
      const result = await withTokenRefresh(() =>
        apiClient.createWorkspace({ name: name.trim() })
      );
      if (result.success && result.workspace) {
        setSelectedWorkspace(result.workspace.id);
        // Force reload workspaces
        window.dispatchEvent(new CustomEvent('workspaceCreated', { detail: { workspaceId: result.workspace.id } }));
      }
      return result;
    } catch (error) {
      console.error('[Hub] Error creating workspace:', error);
      return { success: false };
    }
  };
  
  // Tutorial launcher handlers
  const handleStartTutorial = () => {
    setShowTutorialLauncher(false);
    window.dispatchEvent(new CustomEvent('startCloudGatewayTutorial'));
  };
  
  const handleDismissTutorial = () => {
    try { localStorage.setItem('gatewayTutorialDismissed', 'true'); } catch { /* ignore */ }
    setShowTutorialLauncher(false);
  };
  
  const handleTutorialStepChange = (step: number) => {
    setTutorialStep(step);
    try { localStorage.setItem('gatewayTutorialStep', String(step)); } catch { /* ignore */ }
  };

  // Last conversation state
  const [lastConversation, setLastConversation] = useState<ConversationTypes.ConversationDTO | null>(null);
  const [loadingLastChat, setLoadingLastChat] = useState(true);
  
  // Load last conversation
  useEffect(() => {
    const loadLastConversation = async () => {
      try {
        const chatParams: any = { take: 1 };
        if (currentWorkspaceId && currentWorkspaceId !== 'ALL') {
          chatParams.workspaceId = currentWorkspaceId;
        }
        
        chatParams.userId = globalData?.userProfile?.id || '';
        chatParams.showDeleted = false;
        const response = await apiClient.getConversations(chatParams);
        const conversations = response.conversations || [];
        
        if (conversations.length > 0) {
          setLastConversation(conversations[0]);
        }
      } catch (err) {
        console.error('Failed to load last conversation:', err);
      } finally {
        setLoadingLastChat(false);
      }
    };

    if (authState?.token) {
      loadLastConversation();
    }
  }, [authState?.token]);

  // Les 3 grandes "portes" du Hub - Sas d'entrée
  const hubDoors = [
    {
      id: 'chat-engine',
      title: 'Private Chat',
      description: 'AI-powered conversational interface for intelligent assistance',
      icon: ChatIconCustom,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea',
      onClick: () => handleTabChange({} as React.SyntheticEvent, TabIndex.Chat),
    },
    {
      id: 'organization',
      title: 'Organization',
      description: 'Manage your team, settings, billing and subscription plans',
      icon: OrganizationIcon,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c',
      onClick: () => handleTabChange({} as React.SyntheticEvent, TabIndex.Organization),
    },
    {
      id: 'gateway-hub',
      title: 'Flashgate',
      description: 'Access your cloud resources, repositories, AI services and infrastructure',
      icon: HubIcon,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe',
      onClick: () => handleTabChange({} as React.SyntheticEvent, TabIndex.Home),
    },
    {
      id: 'isp-market',
      title: 'ISP Market',
      description: 'Buy, sell, and manage resource units with Infrastructure Service Providers',
      icon: DepinHubIcon,
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      color: '#11998e',
      onClick: () => handleTabChange({} as React.SyntheticEvent, TabIndex.StorageUnitsDePIN),
    },
  ];

  // Profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  
  // Profile menu state (for sign out dropdown)
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileMenuAnchor);
  
  // Support modal state
  const [showSupportModal, setShowSupportModal] = useState(false);
  
  // Upgrade modal state for Individual accounts
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  
  // Create workspace modal state
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false);
  
  // Open profile modal
  const openProfileModal = () => {
    setProfileModalOpen(true);
  };
  
  // Handle upgrade click for Individual accounts
  const handleUpgradeClick = (featureName: string) => {
    setUpgradeFeatureName(featureName);
    setShowUpgradeModal(true);
  };
  
  // Handle create workspace
  const handleCreateWorkspaceClick = () => {
    setShowCreateWorkspaceModal(true);
  };
  
  const handleCreateWorkspace = async (name: string) => {
    try {
      const result = await withTokenRefresh(() => 
        apiClient.createWorkspace({ name: name.trim() })
      );
      return result;
    } catch (error) {
      console.error('[Hub] Error creating workspace:', error);
      return { success: false };
    }
  };
  
  const handleCreateWorkspaceComplete = async (workspaceId: string) => {
    setShowCreateWorkspaceModal(false);
    // Dispatch event to refresh workspaces and select new one
    window.dispatchEvent(new CustomEvent('workspaceCreated', { detail: { workspaceId } }));
    // Navigate to the gateway
    handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
  };

  // Notifications panel state (server-side read state via API)
  const [showNotifications, setShowNotifications] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [systemEvents, setSystemEvents] = useState<SystemEventTypes.SystemEventResponse[]>([]);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);

  // Helper: get notification icon based on event type
  const getNotificationIcon = (event: string): string => {
    const evt = event.toLowerCase();
    if (evt === 'created') return checkNotifsIcon;
    if (evt === 'updated' || evt === 'changed') return reloadNotifsIcon;
    if (evt === 'deleted' || evt === 'invalid') return warningNotifsIcon;
    return checkNotifsIcon;
  };

  const { isOrgManager } = usePermissions();
  const currentUserId = globalData?.userProfile?.id || '';

  // Load system events for notifications
  const loadSystemEvents = useCallback(async () => {
    if (!authState?.token) return;
    setEventsLoading(true);
    try {
      const data: SystemEventTypes.SystemEventQueryRequest = {
        skip: 0,
        take: 50,
      };
      const res = await withTokenRefresh(() => apiClient.getSystemEvents(data));
      if (res && Array.isArray(res.events)) {
        setSystemEvents(res.events);
      }
    } catch (err) {
      /* ignore */
    } finally {
      setEventsLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load events on mount (to show badge count)
  useEffect(() => {
    loadSystemEvents();
  }, [loadSystemEvents]);

  // Reload events when notifications panel opens
  useEffect(() => {
    if (showNotifications) {
      loadSystemEvents();
    }
  }, [showNotifications, loadSystemEvents]);

  // Close notifications when clicking outside
  useEffect(() => {
    if (!showNotifications) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.notifications-panel-hub') && !target.closest('[title="Notifications"]')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#F9FAFB',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Navbar - Full Width */}
      <div
        style={{
          width: '100%',
          height: '64px',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E9EAEB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        {/* Left: Logo only (no back button on Hub) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img 
            src="/logoblack.png" 
            alt="Flashgate"
            style={{ height: '32px' }}
          />
        </div>

        {/* Right: Action Icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Notifications */}
          <Tooltip 
            title="Notifications" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': { color: '#1F2937' },
                },
              },
            }}
          >
            <button
              ref={notificationsButtonRef}
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                position: 'relative',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <img src={bellSvg} alt="Notifications" style={{ width: 20, height: 20 }} />
              {/* Unread notification badge */}
              {(() => {
                const unreadCount = systemEvents.filter((ev) => ev.showUnread).length;
                if (unreadCount === 0) return null;
                const displayCount = unreadCount > 99 ? '+99' : String(unreadCount);
                return (
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 5px',
                      borderRadius: '9px',
                      backgroundColor: '#617BFF',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {displayCount}
                  </span>
                );
              })()}
            </button>
          </Tooltip>

          {/* Support - opens modal like Header.tsx */}
          <Tooltip 
            title="Support" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': { color: '#1F2937' },
                },
              },
            }}
          >
            <button
              onClick={() => setShowSupportModal(true)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <img src={supportSvg} alt="Support" style={{ width: 20, height: 20 }} />
            </button>
          </Tooltip>

          {/* Documentation - opens docs.flashback.tech like Header.tsx */}
          <Tooltip 
            title="Documentation" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': { color: '#1F2937' },
                },
              },
            }}
          >
            <button
              onClick={() => {
                try {
                  window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer');
                } catch {
                  // Ignore errors
                }
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <img src={docsSvg} alt="Docs" style={{ width: 20, height: 20 }} />
            </button>
          </Tooltip>

          {/* Profile */}
          <Tooltip 
            title="Profile" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': { color: '#1F2937' },
                },
              },
            }}
          >
            <button
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#617BFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {(authState?.user?.name || globalData.userProfile?.name || 'U').charAt(0).toUpperCase()}
              </div>
            </button>
          </Tooltip>
          
          {/* Profile Menu with Sign out */}
          <Menu
            anchorEl={profileMenuAnchor}
            open={profileMenuOpen}
            onClose={() => setProfileMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: 160,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => {
              setProfileMenuAnchor(null);
              setProfileModalOpen(true);
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => handleSignOut()}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </div>

        {/* Notifications Panel - same as Header.tsx */}
        {showNotifications && (
          <div
            className="notifications-panel-hub fixed z-50 pointer-events-auto"
            style={{
              top: '70px',
              right: notificationsButtonRef.current 
                ? `${window.innerWidth - notificationsButtonRef.current.getBoundingClientRect().right}px`
                : '20px',
            }}
          >
            <div
              className="bg-white border border-[#E9EAEB] p-4 shadow-lg overflow-hidden flex flex-col"
              style={{ width: 540, height: 434, borderRadius: 12 }}
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <span style={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
                  Notifications
                </span>
                <div className="flex items-center gap-2">
                  {systemEvents.some((ev) => ev.showUnread) && (
                    <button
                      type="button"
                      className="text-[12px] text-[#617BFF] hover:underline font-medium"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        const unreadEvents = systemEvents.filter((ev) => ev.showUnread);
                        unreadEvents.forEach((ev) => {
                          withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* noop */ });
                        });
                        setSystemEvents((prev) =>
                          prev.map((e) => (e.showUnread ? { ...e, showUnread: false } : e))
                        );
                      }}
                    >
                      Mark all as Read
                    </button>
                  )}
                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setShowNotifications(false)}
                    title="Close"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 mt-1">
                {eventsLoading ? (
                  <div className="text-sm text-[#475467] py-2">Loading…</div>
                ) : systemEvents.length === 0 ? (
                  <div className="text-sm text-[#475467] py-2">No recent events</div>
                ) : (
                  <ul className="h-full overflow-auto divide-y divide-[#E9EAEB] pr-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {systemEvents
                      .filter((ev) => {
                        if (ev.showUnread) return true;
                        const age = Date.now() - new Date(ev.timestamp).getTime();
                        return age < 24 * 60 * 60 * 1000;
                      })
                      .map((ev) => (
                        <li
                          key={ev.id}
                          className={`py-2.5 px-2 transition-all duration-200 rounded-lg ${
                            ev.showUnread
                              ? 'bg-[rgba(97,123,255,0.06)] border-l-2 border-[#617BFF]'
                              : 'hover:bg-[#FAFAFA]'
                          }`}
                          onMouseEnter={() => {
                            if (ev.showUnread) {
                              withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* ignore */ });
                              setSystemEvents((prev) =>
                                prev.map((e) => (e.id === ev.id ? { ...e, showUnread: false } : e))
                              );
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={getNotificationIcon(ev.event)}
                              alt=""
                              className="flex-shrink-0"
                              style={{ width: 48, height: 48 }}
                            />
                            <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-[#111827] capitalize truncate">
                                  {ev.context} · {ev.event}
                                </div>
                                {(() => {
                                  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                                  let nameOrBucket = '';
                                  try {
                                    if (ev.jsonData) {
                                      const obj = JSON.parse(ev.jsonData);
                                      nameOrBucket = obj.name || obj.bucket || '';
                                    }
                                  } catch {
                                    /* ignore */
                                  }
                                  const ctx = ev.context.toLowerCase();
                                  const evt = ev.event.toLowerCase();

                                  let action = '';
                                  if (evt === 'created') action = 'successfully created';
                                  else if (evt === 'updated') action = ctx === 'usersettings' ? 'were changed' : 'updated';
                                  else if (evt === 'deleted') action = 'deleted';
                                  else action = evt;

                                  let typeLabel = '';
                                  if (ctx === 'bucket') typeLabel = 'Bucket';
                                  else if (ctx === 'workspace') typeLabel = 'Workspace';
                                  else if (ctx === 'repo') typeLabel = 'Repository';
                                  else if (ctx === 'usersettings') typeLabel = 'User settings';
                                  else if (ctx === 'apikey') typeLabel = 'API Key';
                                  else typeLabel = cap(ctx);

                                  const handleItemClick = (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setShowNotifications(false);
                                    if (ctx === 'bucket') {
                                      navigate('/cloud-storage/buckets');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.StorageUnits);
                                    } else if (ctx === 'repo') {
                                      navigate('/repositories/inventory');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.StorageRepos);
                                    } else if (ctx === 'apikey') {
                                      navigate('/cloud-storage/overview');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Dashboard);
                                    } else if (ctx === 'workspace') {
                                      navigate('/workspace/home');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
                                    } else if (ctx === 'organization') {
                                      navigate('/organization');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
                                    } else if (ctx === 'usersettings' || ctx === 'user') {
                                      window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile' } }));
                                    }
                                  };

                                  return (
                                    <div className="text-[12px] text-[#6B7280] truncate mt-0.5">
                                      {nameOrBucket ? (
                                        <>
                                          {typeLabel}{' '}
                                          <span
                                            onClick={handleItemClick}
                                            style={{
                                              color: '#4766FF',
                                              cursor: 'pointer',
                                              fontWeight: 600,
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.textDecoration = 'underline')
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.textDecoration = 'none')
                                            }
                                          >
                                            "{nameOrBucket}"
                                          </span>{' '}
                                          {action}
                                        </>
                                      ) : (
                                        `${typeLabel} ${action}`
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="flex flex-col items-end gap-0.5 flex-none">
                                <div className="text-[12px] text-[#6B7280] whitespace-nowrap">
                                  {new Date(ev.timestamp).toLocaleString()}
                                </div>
                                {isOrgManager && ev.userId !== currentUserId && ev.userName && (
                                  <div className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                                    By: {ev.userName}
                                  </div>
                                )}
                                {!ev.showUnread && (
                                  <span className="text-[11px] text-[#22C55E] flex items-center gap-0.5">
                                    ✓ Read
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              {/* Footer: View All Notifications */}
              {isOrgManager && (
                <div className="shrink-0 border-t border-[#E9EAEB] pt-3 mt-2">
                  <button
                    type="button"
                    className="w-full text-center text-[13px] font-medium text-[#617BFF] hover:text-[#4F63CC] hover:underline py-1"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/activity');
                      handleTabChange({} as React.SyntheticEvent, TabIndex.Activity);
                    }}
                  >
                    View All Notifications
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Unified Setup Banner - shown when 2FA is not enabled (after 1min delay, with 30min cooldown) */}
      {!is2FAEnabled && isSetupBannerVisible && !isSetupBannerDismissed && (
        <Security2FAWarning
          sticky
          onSetup2FA={() => {
            const step = isProfileIncomplete ? 0 : 3;
            window.dispatchEvent(new CustomEvent('openAccountSettingsTutorial', { detail: { initialStep: step } }));
          }}
          onDismiss={handleSetupBannerDismiss}
          message="Profile and 2FA incomplete. Setup now."
        />
      )}

      {/* Main Content - Centered */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}
      >
      {/* En-tête centré */}
      <div style={{ textAlign: 'left', marginBottom: '40px', maxWidth: '1100px', width: '100%' }}>
        <Typography
          style={{
            fontSize: '42px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Welcome to Flashgate{authState?.user?.name ? `, ${authState.user.name}` : (globalData.userProfile?.name ? `, ${globalData.userProfile.name}` : '')}
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Choose where you want to go
        </Typography>
      </div>

      {/* Quicklinks - Retour rapide aux derniers endroits utilisés */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
          maxWidth: '1100px',
          marginBottom: '32px',
        }}
      >
        {/* Quicklink: Last Workspace - Opens create modal if no workspaces */}
        <div
          onClick={() => {
            if (hasNoWorkspaces) {
              handleCreateWorkspaceClick();
            } else {
              handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
            }
          }}
          style={{
            flex: 1,
            backgroundColor: hasNoWorkspaces ? '#F9FAFB' : 'white',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
            opacity: hasNoWorkspaces ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!hasNoWorkspaces) {
              e.currentTarget.style.borderColor = '#4facfe';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.15)';
            } else {
              e.currentTarget.style.borderColor = '#D1D5DB';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E5E7EB';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              background: hasNoWorkspaces ? '#E5E7EB' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {hasNoWorkspaces ? (
              <LockIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
            ) : (
              <WorkspacesIcon sx={{ fontSize: 24, color: 'white' }} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: hasNoWorkspaces ? '#9CA3AF' : '#1F2937',
                marginBottom: '2px',
              }}
            >
              Workspace
            </Typography>
            <Typography
              style={{
                fontSize: '11px',
                color: '#9CA3AF',
                marginBottom: '2px',
              }}
            >
              {hasNoWorkspaces ? 'locked' : 'lastly visited'}
            </Typography>
            <Typography
              style={{
                fontSize: '12px',
                color: hasNoWorkspaces ? '#9CA3AF' : '#667eea',
                fontWeight: 600,
              }}
            >
              {hasNoWorkspaces 
                ? 'Create a Workspace'
                : globalData?.workspaces?.find((w: any) => w.id === globalData?.selectedWorkspaceId)?.name || 'Return to your workspace'}
            </Typography>
          </div>
        </div>

        {/* Quicklink: Last Chat - Grayed for Individual accounts */}
        <div
          onClick={() => {
            if (isIndividualAccount) {
              handleUpgradeClick('Private Chat');
            } else {
              handleTabChange({} as React.SyntheticEvent, TabIndex.Chat);
            }
          }}
          style={{
            flex: 1,
            backgroundColor: isIndividualAccount ? '#F9FAFB' : 'white',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
            opacity: isIndividualAccount ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isIndividualAccount) {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
            } else {
              e.currentTarget.style.borderColor = '#D1D5DB';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E5E7EB';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              background: isIndividualAccount ? '#E5E7EB' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isIndividualAccount ? (
              <LockIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
            ) : (
              <ChatIconCustom size={24} color="white" />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: isIndividualAccount ? '#9CA3AF' : '#1F2937',
                marginBottom: '2px',
              }}
            >
              Conversation
            </Typography>
            <Typography
              style={{
                fontSize: '11px',
                color: '#9CA3AF',
                marginBottom: '2px',
              }}
            >
              {isIndividualAccount ? 'locked' : 'lastly visited'}
            </Typography>
            <Typography
              style={{
                fontSize: '12px',
                color: isIndividualAccount ? '#9CA3AF' : '#667eea',
                fontWeight: 600,
              }}
            >
              {isIndividualAccount 
                ? 'Organization Account required'
                : loadingLastChat
                  ? 'Loading...'
                  : lastConversation?.repo?.name
                    ? lastConversation.repo.name
                    : 'Start a new conversation'}
            </Typography>
          </div>
        </div>
      </div>

      {/* Les 3 grandes portes */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          width: '100%',
          maxWidth: '1100px',
          marginBottom: '40px',
        }}
      >
        {hubDoors.map((door) => {
          const IconComponent = door.icon;
          // Check if this door should be locked for Individual accounts
          const isLockedForIndividual = isIndividualAccount && (door.id === 'chat-engine' || door.id === 'organization');
          const isComingSoon = !!(door as any).comingSoon;
          const isBlocked = isLockedForIndividual || isComingSoon;
          
          return (
            <div
              key={door.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isComingSoon) return;
                if (isLockedForIndividual) {
                  handleUpgradeClick(door.title);
                  return;
                }
                console.log('[Hub] Opening door:', door.title);
                if (door.onClick) {
                  door.onClick();
                }
              }}
              role="button"
              tabIndex={isComingSoon ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isComingSoon) return;
                  if (isLockedForIndividual) {
                    handleUpgradeClick(door.title);
                    return;
                  }
                  if (door.onClick) {
                    door.onClick();
                  }
                }
              }}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: '40px 32px',
                cursor: isComingSoon ? 'default' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '340px',
                position: 'relative',
                overflow: 'hidden',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => {
                if (isComingSoon) return;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = isLockedForIndividual ? '#617BFF' : door.color;
                e.currentTarget.style.boxShadow = isLockedForIndividual 
                  ? '0 12px 24px rgba(97, 123, 255, 0.2)' 
                  : `0 12px 24px ${door.color}20`;
              }}
              onMouseLeave={(e) => {
                if (isComingSoon) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Coming Soon overlay */}
              {isComingSoon && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(1px)',
                    zIndex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      backgroundColor: '#F3F4F6',
                      color: '#6B7280',
                      fontSize: '13px',
                      fontWeight: 600,
                      padding: '6px 16px',
                      borderRadius: '20px',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    Coming Soon
                  </span>
                </div>
              )}

              {/* Locked overlay for Individual accounts - lighter blur */}
              {isLockedForIndividual && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(1px)',
                    zIndex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingBottom: '24px',
                  }}
                >
                  {/* Lock badge positioned over the icon */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '85px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      border: '2px solid #E5E7EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    <LockIcon sx={{ fontSize: 18, color: '#617BFF' }} />
                  </div>
                  
                  {/* Bottom content */}
                  <Typography
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      textAlign: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    Organization Account Required
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#617BFF',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      borderRadius: '8px',
                      px: 2.5,
                      py: 0.75,
                      '&:hover': {
                        backgroundColor: '#5068e0',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              )}

              {/* Gradient background effect */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '140px',
                  background: door.gradient,
                  opacity: isLockedForIndividual ? 0.04 : 0.08,
                  pointerEvents: 'none',
                }}
              />

              {/* Icône */}
              <div
                style={{
                  width: '90px',
                  height: '90px',
                  borderRadius: '20px',
                  background: door.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '28px',
                  position: 'relative',
                  boxShadow: `0 8px 24px ${door.color}30`,
                  pointerEvents: 'none',
                  opacity: isLockedForIndividual ? 0.75 : 1,
                }}
              >
                <IconComponent size={44} color="white" />
              </div>

              {/* Titre */}
              <Typography
                style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: isLockedForIndividual ? '#9CA3AF' : '#1F2937',
                  marginBottom: '12px',
                  fontFamily: 'Inter',
                  pointerEvents: 'none',
                }}
              >
                {door.title}
              </Typography>

              {/* Description */}
              <Typography
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: isLockedForIndividual ? '#D1D5DB' : '#6B7280',
                  lineHeight: '22px',
                  fontFamily: 'Inter',
                  maxWidth: '280px',
                  pointerEvents: 'none',
                  flex: 1,
                }}
              >
                {door.description}
              </Typography>


              {/* Arrow - hidden for locked cards */}
              {!isLockedForIndividual && (
              <div
                style={{
                  marginTop: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: door.color,
                  fontSize: '14px',
                  fontWeight: 600,
                  pointerEvents: 'none',
                }}
              >
                <span>Enter</span>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Workspaces info en bas - Hidden if no workspaces */}
      {!hasNoWorkspaces && (
      <div
        style={{
          textAlign: 'center',
          color: '#9CA3AF',
          fontSize: '13px',
          fontFamily: 'Inter',
        }}
      >
        {globalData?.workspaces?.length || 0} workspace
        {(globalData?.workspaces?.length || 0) !== 1 ? 's' : ''} available
      </div>
      )}
      </div>

      {/* Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        initialSection="Profile"
      />

      {/* Support Modal */}
      <SupportModal 
        open={showSupportModal} 
        onClose={() => setShowSupportModal(false)}
        apiClient={apiClient}
        withTokenRefresh={withTokenRefresh}
        userEmail={authState?.user?.email || ''}
      />
      
      {/* Upgrade Prompt Modal for Individual accounts */}
      <UpgradePromptModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
      
      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        open={showCreateWorkspaceModal}
        onClose={() => setShowCreateWorkspaceModal(false)}
        onComplete={handleCreateWorkspaceComplete}
        onCreateWorkspace={handleCreateWorkspace}
      />
      
      {/* 2FA Required Modal */}
      <Security2FARequiredModal
        open={show2FARequiredModal}
        onClose={() => setShow2FARequiredModal(false)}
        onSetup2FA={handle2FASetup}
      />
      
      {/* Flashgate intro modal */}
      <CloudGatewayIntroModal
        open={showGatewayIntro}
        onComplete={(workspaceId: string) => {
          handleGatewayIntroComplete();
        }}
        onCreateWorkspace={handleCreateWorkspaceFromIntro}
        userName={globalData?.userProfile?.firstName || authState?.user?.name}
        onBackToMain={() => {
          setShowGatewayIntro(false);
          navigate('/');
        }}
      />
      
      {/* Tutorial launcher removed - now only shown in Flashgate */}
    </div>
  );
};

export default Hub;
